/**
 * Les quatre classements (artefact « CyberNovel · Le classement » du 1er septembre).
 * Le serveur renvoie une entrée par joueur ; ici on trie et on met en forme par onglet.
 * À égalité, les boucliers départagent, puis le pseudo.
 */

export interface LeaderboardPlayer {
  pseudo: string;
  color: string;
  shields: number;
  /** Date ISO de fin de campagne, null tant que le joueur n'a pas fini. */
  finishedAt: string | null;
  allies: number;
  falls: number;
  isMe: boolean;
}

export interface LeaderboardMe {
  pseudo: string | null;
  color: string;
  /** False pour un compte qui voit l'écran sans y figurer (testeur, admin…). */
  ranked: boolean;
  /** Ses propres chiffres, même hors classement (null sans campagne). */
  entry: LeaderboardPlayer | null;
}

export interface LeaderboardResponse {
  scenarios: { id: string; title: string }[];
  scenarioId: string | null;
  shieldsMax: number;
  players: LeaderboardPlayer[];
  me: LeaderboardMe;
}

export type LeaderboardViewKey = 'complet' | 'premier' | 'allies' | 'chutes';

/** La valeur dans la gélule du joueur : une partie en gras, entourée de texte. */
export interface SummaryParts {
  before?: string;
  strong: string;
  after?: string;
}

export interface LeaderboardView {
  key: LeaderboardViewKey;
  label: string;
  compare: (a: LeaderboardPlayer, b: LeaderboardPlayer) => number;
  value: (player: LeaderboardPlayer, shieldsMax: number) => string;
  summary: (player: LeaderboardPlayer, shieldsMax: number) => SummaryParts;
}

const byPseudo = (a: LeaderboardPlayer, b: LeaderboardPlayer) =>
  a.pseudo.localeCompare(b.pseudo, 'fr', { sensitivity: 'base' });
const byShields = (a: LeaderboardPlayer, b: LeaderboardPlayer) => b.shields - a.shields || byPseudo(a, b);

function plural(count: number, word: string): string {
  return `${word}${count > 1 ? 's' : ''}`;
}

const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/Paris',
});

/** « 28 août », « 1er sept. » (heure de Paris). */
export function formatFinishDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = dateFormatter.formatToParts(date);
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  return `${day === '1' ? '1er' : day} ${month}`.trim();
}

/** « 1er », « 2e »… séparés pour mettre le suffixe en exposant. */
export function formatRank(rank: number): { number: string; suffix: string } {
  return { number: String(rank), suffix: rank === 1 ? 'er' : 'e' };
}

export function formatShields(player: LeaderboardPlayer, shieldsMax: number): string {
  return shieldsMax > 0 ? `${player.shields} / ${shieldsMax}` : String(player.shields);
}

export const LEADERBOARD_VIEWS: LeaderboardView[] = [
  {
    key: 'complet',
    label: 'Le plus complet',
    compare: byShields,
    value: formatShields,
    summary: (player, shieldsMax) => ({
      strong: String(player.shields),
      after: shieldsMax > 0 ? ` ${plural(player.shields, 'bouclier')} sur ${shieldsMax}` : ` ${plural(player.shields, 'bouclier')}`,
    }),
  },
  {
    key: 'premier',
    label: 'Le premier arrivé',
    compare: (a, b) => {
      if (a.finishedAt && b.finishedAt) {
        return a.finishedAt.localeCompare(b.finishedAt) || byShields(a, b);
      }
      if (a.finishedAt) return -1;
      if (b.finishedAt) return 1;
      return byShields(a, b);
    },
    value: (player) => (player.finishedAt ? `fini le ${formatFinishDate(player.finishedAt)}` : 'en cours'),
    summary: (player) =>
      player.finishedAt
        ? { before: 'fini le ', strong: formatFinishDate(player.finishedAt) }
        : { strong: 'en cours' },
  },
  {
    key: 'allies',
    label: "Le plus d'alliés",
    compare: (a, b) => b.allies - a.allies || byShields(a, b),
    value: (player) => `${player.allies} ${plural(player.allies, 'allié')}`,
    summary: (player) => ({ strong: String(player.allies), after: ` ${plural(player.allies, 'allié')}` }),
  },
  {
    key: 'chutes',
    label: 'Le plus de chutes',
    compare: (a, b) => b.falls - a.falls || byShields(a, b),
    value: (player) => `${player.falls} ${plural(player.falls, 'chute')}`,
    summary: (player) => ({ strong: String(player.falls), after: ` ${plural(player.falls, 'chute')}` }),
  },
];

export function getLeaderboardView(key: LeaderboardViewKey): LeaderboardView {
  return LEADERBOARD_VIEWS.find((view) => view.key === key) ?? (LEADERBOARD_VIEWS[0] as LeaderboardView);
}

export function rankPlayers(players: LeaderboardPlayer[], key: LeaderboardViewKey): LeaderboardPlayer[] {
  return [...players].sort(getLeaderboardView(key).compare);
}
